import type { LDFlagSet } from "launchdarkly-react-client-sdk";

import { Button, ErrorContainer, Heading, IconInfo, IconPlus } from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useGetMyAccount, useGetMyWriters } from "@/api/queries";
import { hasAdminRights } from "@/auth/shared";
import useWritersTour from "@/components/tour/WritersTour";
import useGetColours from "@/helpers/getColours";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import WritersTable from "./WritersTable";

function Writers() {
  const { t } = useTranslation();
  const selectedAccount = useAppStore(s => s.selectedAccount);
  const navigate = useNavigate();

  const flags = useFlags();
  const showTourFlag = flags["pd-show-tutorial"] as LDFlagSet;

  const { startTour, endTour } = useWritersTour();

  const startWritersTour = () => {
    startTour();
  };

  const isTunecore = useUIStateStore(state => state.isTunecore);

  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount as string,
  });

  const writers = useGetMyWriters({ aac: selectedAccount as string });

  const { highlightColour } = useGetColours();

  const adminRights = hasAdminRights(myAccount?.data);

  useEffect(() => {
    if (selectedAccount !== null) {
      void writers.refetch();
    }
  }, [selectedAccount, writers]);

  return (
    <div className="flex flex-wrap w-full">
      <div className="w-full lg:min-h-screen-minus-header">
        <div className="mx-4 my-12 lg:mx-12 xl:mx-20 2xl:mx-40">
          <div className="flex flex-wrap items-baseline justify-between">
            <div className="mb-4
            "
            >
              <Heading as="h3">{t("writers:writers-header", "Writers")}</Heading>
              {Boolean(showTourFlag) && (
                <Button
                  variant="link"
                  onClick={startWritersTour}
                  icon={<IconInfo className="w-5 h-5 mb-1 stroke-ink-accent" />}
                  label={t("tutorial:need-help")}
                />
              )}
            </div>
            <div>
              <Button
                variant="primary"
                icon={<IconPlus size="md" className="stroke-ink-primary" />}
                id="btnWriterAddNew"
                disabled={!adminRights}
                onClick={() => {
                  void endTour();
                  if (adminRights) {
                    void navigate({ to: "/writers/create" });
                  }
                }}
                label={t("writers:add-new-writer-button2")}
              />
            </div>
          </div>
          <div
            id="writersTable"
            className={`mt-6 ${isTunecore ? "bg-layer-inverse" : "bg-white"} rounded-lg p-4 lg:p-8`}
          >
            {writers.isError && (
              <ErrorContainer message={String(t("writers:error-loading-writers", "Failed to load writers. Please refresh and try again."))} />
            )}
            {!writers.isError && (
              <WritersTable
                highlightColour={highlightColour}
                data={writers.data?.writers ?? []}
                adminRights={adminRights}
                onWriterUpdated={() => { void writers.refetch(); }}
                isLoading={writers.isLoading}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
export default Writers;
